import { WidgetErrorFallback } from "./WidgetErrorFallback";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface WidgetErrorBoundaryProps {
  /** Named in the fallback so the user knows which card failed. */
  widgetName: string;
  children: ReactNode;
}

interface WidgetErrorBoundaryState {
  hasError: boolean;
}

/**
 * Contains a widget's crash to its own card.
 *
 * Ten independent widgets each fetch their own data and render their own shapes,
 * so a malformed response or a missing field in any one of them would otherwise
 * unmount the entire canvas — and take the other nine working widgets, the
 * customize controls and the unsaved layout with it. A boundary per card turns
 * that into one tile saying it could not load.
 *
 * Has to be a class: error boundaries are the one thing React still has no hook
 * for. Kept deliberately small for that reason — it catches, it reports, it
 * offers a retry, and nothing else.
 */
export class WidgetErrorBoundary extends Component<
  WidgetErrorBoundaryProps,
  WidgetErrorBoundaryState
> {
  state: WidgetErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): WidgetErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Named so the console points at the widget rather than at the grid.
    console.error(`Widget "${this.props.widgetName}" crashed`, error, info);
  }

  componentDidUpdate(previous: WidgetErrorBoundaryProps) {
    // A different widget in the same slot starts from a clean sheet.
    if (previous.widgetName !== this.props.widgetName && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <WidgetErrorFallback onRetry={() => this.setState({ hasError: false })} />
    );
  }
}
